import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  CheckCircle,
  XCircle,
  Info,
} from "lucide-react";

const ValidAnagramVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Example 1 (True)",
      s: "anagram",
      t: "nagaram",
    },
    {
      id: 2,
      name: "Example 2 (False)",
      s: "rat",
      t: "car",
    },
    {
      id: 3,
      name: "Different Lengths",
      s: "a",
      t: "ab",
    },
    {
      id: 4,
      name: "Duplicates",
      s: "aacc",
      t: "ccac",
    },
    {
      id: 5,
      name: "Empty Strings",
      s: "",
      t: "",
    },
    {
      id: 6,
      name: "False with Duplicates",
      s: "awesome",
      t: "awesom",
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customS, setCustomS] = useState("");
  const [customT, setCustomT] = useState("");
  const [s, setS] = useState(defaultTestCases[0].s);
  const [t, setT] = useState(defaultTestCases[0].t);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function isAnagram(s, t):", indent: 0 },
    { line: "if s.length != t.length: return false", indent: 1 },
    { line: "", indent: 0 },
    { line: "sMap = create frequency map of s", indent: 1 },
    { line: "tMap = create frequency map of t", indent: 1 },
    { line: "", indent: 0 },
    { line: "for char in sMap:", indent: 1 },
    { line: "if sMap[char] != tMap[char]:", indent: 2 },
    { line: "return false", indent: 3 },
    { line: "", indent: 0 },
    { line: "return true", indent: 1 },
  ];

  const optimizedPseudoCode = [
    { line: "function isAnagram(s, t):", indent: 0 },
    { line: "if s.length != t.length: return false", indent: 1 },
    { line: "", indent: 0 },
    { line: "freqMap = {}", indent: 1 },
    { line: "for char in s:", indent: 1 },
    { line: "freqMap[char]++", indent: 2 },
    { line: "", indent: 0 },
    { line: "for char in t:", indent: 1 },
    { line: "if !freqMap[char]: return false", indent: 2 },
    { line: "freqMap[char]--", indent: 2 },
    { line: "", indent: 0 },
    { line: "return true", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (s, t) => {
    const allSteps = [];
    let stepCount = 0;
    let freqMap = {};
    let result = null;

    const addStep = (description, highlightLine, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description,
        highlightLine,
        freqMap: { ...freqMap },
        result,
        ...details,
      });
    };

    addStep("Start: Check if strings have equal length.", 1);
    if (s.length !== t.length) {
      addStep(
        `Lengths are different (${s.length} != ${t.length}). Not an anagram.`,
        1,
        { isFinal: true, result: false }
      );
      return allSteps;
    }
    addStep(`Lengths are equal (${s.length} == ${t.length}). Proceed.`, 1);

    addStep("Build frequency map for string 's'.", 4);
    for (let i = 0; i < s.length; i++) {
      const char = s[i];
      freqMap[char] = (freqMap[char] || 0) + 1;
      addStep(
        `Processing s[${i}]: '${char}'. Count is now ${freqMap[char]}.`,
        5,
        { s_idx: i }
      );
    }

    addStep("Map for 's' built. Now check against string 't'.", 7);
    for (let i = 0; i < t.length; i++) {
      const char = t[i];
      addStep(`Processing t[${i}]: '${char}'. Checking map.`, 8, {
        s_idx: s.length - 1,
        t_idx: i,
      });
      if (!freqMap[char] || freqMap[char] === 0) {
        addStep(
          `'${char}' not found in map or count is zero. Not an anagram.`,
          8,
          { s_idx: s.length - 1, t_idx: i, isFinal: true, result: false }
        );
        return allSteps;
      }
      freqMap[char]--;
      addStep(`Found '${char}'. Decrementing count to ${freqMap[char]}.`, 9, {
        s_idx: s.length - 1,
        t_idx: i,
      });
    }

    addStep(
      "Finished checking 't'. All characters matched. It's an anagram!",
      11,
      { isFinal: true, result: true }
    );
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(s, t);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [s, t]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setS(testCases[index].s);
    setT(testCases[index].t);
  };

  const addCustomTestCase = () => {
    try {
      const newTestCase = {
        id: Date.now(),
        name: `Custom`,
        s: customS,
        t: customT,
      };
      setTestCases([...testCases, newTestCase]);
      setCustomS("");
      setCustomT("");
    } catch (error) {
      alert(`Invalid input: ${error.message}`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <CheckCircle className="text-blue-500" /> Valid Anagram
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given two strings `s` and `t`, return `true` if `t` is an
                anagram of `s`, and `false` otherwise. An anagram is a word or
                phrase formed by rearranging the letters of a different word or
                phrase, typically using all the original letters exactly once.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      s = "{tc.s}", t = "{tc.t}"
                    </div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customS}
                onChange={(e) => setCustomS(e.target.value)}
                placeholder="String s: anagram"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <input
                type="text"
                value={customT}
                onChange={(e) => setCustomT(e.target.value)}
                placeholder="String t: nagaram"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4 space-y-4">
              <div>
                <p className="text-sm font-semibold mb-2">String s:</p>
                <div className="flex flex-wrap gap-1">
                  {s.split("").map((char, idx) => (
                    <div
                      key={idx}
                      className={`w-10 h-10 flex items-center justify-center border-2 rounded font-mono text-xl font-bold transition-all duration-300 ${
                        currentStepData?.s_idx === idx
                          ? "bg-blue-200 border-blue-400 scale-110"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      {char}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2">String t:</p>
                <div className="flex flex-wrap gap-1">
                  {t.split("").map((char, idx) => (
                    <div
                      key={idx}
                      className={`w-10 h-10 flex items-center justify-center border-2 rounded font-mono text-xl font-bold transition-all duration-300 ${
                        currentStepData?.t_idx === idx
                          ? "bg-yellow-200 border-yellow-400 scale-110"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      {char}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-700">
                Frequency Map
              </h4>
              <div className="p-2 bg-gray-50 rounded mt-1 flex flex-wrap gap-2 justify-center min-h-[40px]">
                {currentStepData &&
                  Object.entries(currentStepData.freqMap).map(([key, val]) => (
                    <span
                      key={key}
                      className="font-mono text-xs p-1 bg-gray-200 rounded"
                    >
                      {key}: {val}
                    </span>
                  ))}
              </div>
            </div>

            {currentStepData?.isFinal && (
              <div
                className={`mt-4 p-3 rounded-lg text-center ${
                  currentStepData.result ? "bg-green-100" : "bg-red-100"
                }`}
              >
                <div
                  className={`flex items-center justify-center font-bold ${
                    currentStepData.result ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {currentStepData.result ? (
                    <CheckCircle className="mr-2" />
                  ) : (
                    <XCircle className="mr-2" />
                  )}
                  Result:{" "}
                  {currentStepData.result ? "Is an Anagram" : "Not an Anagram"}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Frequency Map Algorithm
            </h3>
            <div className="space-y-0.5">
              {optimizedPseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The most efficient way to check for anagrams is to use a frequency
              map (or a simple array of size 26 for only lowercase English
              letters). First, check if the strings have the same length. Then,
              iterate through the first string, `s`, to build a frequency map of
              its characters. Finally, iterate through the second string, `t`,
              decrementing the count for each character. If you encounter a
              character not in the map or its count is already zero, it's not an
              anagram.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(1)
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              N is the length of the strings. Space is O(1) as the map size is
              constant (26 letters).
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Anagrams must have the same length. This is a quick first
                  check to fail early.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The core idea is that two strings are anagrams if and only if
                  they contain the exact same characters with the exact same
                  frequencies.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  A single hash map is more efficient than creating two separate
                  maps and comparing them.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-blue-200 border-2 border-blue-400"></div>
                <span>Processing 's' (building map)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-yellow-200 border-2 border-yellow-400"></div>
                <span>Processing 't' (checking map)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidAnagramVisualizer;
